import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { addListeners, IListeners } from 'decorators/addListeners';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import { WithRouterProps } from 'decorators/withRouter';
import { changePassword, resetUserErrors } from 'actions/user';
import api from 'api/user';
import showNotification from 'components/ui/notification/showNotification';
import { PageAuthWrapperLegacy } from '../components/PageAuthWrapperLegacy';
import { PasswordChangeFormLegacy } from './PasswordChangeFormLegacy';
import Messages from 'constants/rpcTypes';
import path from 'helpers/path';
import Scenarios from 'pages/auth/passwordChangePage/Scenarios';
import { RootState, StoreProps } from 'store';
import { PageAuthWrapper } from '../components/PageAuthWrapper';
import { PasswordChangeForm } from './PasswordChangeForm';

interface ConnectedProps {
  user: RootState['user'];
  settings: RootState['settings'];
}

type Props = ConnectedProps & StoreProps & IntlProps & WithRouterProps;

interface State {
  password: string;
  passwordType: string;
  passwordConfirm: string;
  passwordConfirmType: string;
  isTokenValid: boolean;
}

@addListeners([Messages.Auth_PasswordResetComplete])
class PasswordChangePage extends Component<Props, State> implements IListeners {
  private readonly token;

  constructor(props: Props) {
    super(props);

    this.state = {
      isTokenValid: false,
      password: '',
      passwordType: 'password',
      passwordConfirm: '',
      passwordConfirmType: 'password',
    };

    this.token = this.getUrlParams().get('token');
    props.dispatch(resetUserErrors());
  }

  componentDidMount() {
    this.checkToken(this.token);
  }

  getTitle = () => {
    const { location } = this.props;
    const { scenario } = queryString.parse(location.search);

    switch (scenario) {
      case Scenarios.new:
        return 'auth.changeTemporaryPassword.info.title';
      case Scenarios.renew:
        return 'auth.changeOldPassword.info.title';
      case Scenarios.reset:
        return 'auth.changeResetPassword.info.title';
      default:
        return '';
    }
  };

  changeFields = (key: string, value: string | boolean) => {
    this.setState({
      [key]: value,
    } as Pick<State, keyof State>);
  };

  changePassword = () => {
    const { dispatch } = this.props;
    const { password, passwordConfirm } = this.state;

    dispatch(changePassword({ password, passwordConfirm, token: this.token }));
  };

  getUrlParams = () => {
    if (!this.props.history.location.search) return new URLSearchParams();
    return new URLSearchParams(this.props.history.location.search);
  };

  checkToken = (token: string) => {
    const { history, getTranslate } = this.props;

    api.checkToken(token).then(({ isValid }) => {
      if (isValid) {
        this.setState({ isTokenValid: isValid });
      } else {
        showNotification({
          status: 'error',
          content: (
            <span>
              {getTranslate('auth.passwordChangeExpiredNotification')}
            </span>
          ),
        });
        history.push(path('/login'));
      }
    });
  };

  onEvent = async ({ data, name }) => {
    const { history, getTranslate } = this.props;
    const { status } = data.rpc;

    if (name === Messages.Auth_PasswordResetComplete && status === 'success') {
      showNotification({
        status: 'success',
        content: <span>{getTranslate('auth.passwordChangeNotification')}</span>,
      });
      history.push(path('/login'));
    }
  };

  render() {
    const {
      isTokenValid,
      password,
      passwordType,
      passwordConfirm,
      passwordConfirmType,
    } = this.state;
    const { user, settings } = this.props;
    const { wlId } = settings;

    if (!isTokenValid) return null;

    return wlId === 1 ? (
      <PageAuthWrapper>
        <PasswordChangeForm
          text={this.getTitle()}
          validation={user && user.validationErrors}
          password={password}
          passwordType={passwordType}
          passwordConfirm={passwordConfirm}
          passwordConfirmType={passwordConfirmType}
          settings={settings}
          onChange={this.changeFields}
          onSave={this.changePassword}
        />
      </PageAuthWrapper>
    ) : (
      <PageAuthWrapperLegacy
        title='auth.changePassword.header'
        text={this.getTitle()}
        subtext='auth.changePassword.info.description'>
        <PasswordChangeFormLegacy
          validation={user && user.validationErrors}
          password={password}
          passwordType={passwordType}
          passwordConfirm={passwordConfirm}
          passwordConfirmType={passwordConfirmType}
          settings={settings}
          onChange={this.changeFields}
          onSave={this.changePassword}
        />
      </PageAuthWrapperLegacy>
    );
  }
}

const mapStateToProps = (state: RootState): ConnectedProps => ({
  user: state.user,
  settings: state.settings,
});

export default withRouter(
  connect(mapStateToProps)(addTranslation(PasswordChangePage))
);
